import React, { useCallback } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  Handle,
  Position,
  ConnectionMode,
} from 'reactflow';
import 'reactflow/dist/style.css';

interface RoadmapFlowProps {
  modules: {
    title: string;
    lessons: string[];
  }[];
}

const ModuleNode = ({ data }: { data: { label: string; lessons: string[] } }) => (
  <div className="group relative">
    <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-blue-400 rounded-xl opacity-20 blur-lg group-hover:opacity-30 transition-opacity duration-300"></div>
    <div className="relative px-6 py-4 rounded-xl bg-white border border-blue-100 shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="font-medium text-gray-800">{data.label}</div>
      <div className="mt-2 text-xs text-gray-500">{data.lessons.length} lessons</div>
      <Handle 
        type="target" 
        position={Position.Top} 
        className="!bg-blue-400 !w-3 !h-3 !-top-1.5 hover:!bg-blue-500 transition-colors duration-200" 
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        className="!bg-blue-400 !w-3 !h-3 !-bottom-1.5 hover:!bg-blue-500 transition-colors duration-200" 
      />
    </div>
  </div>
);

const nodeTypes = {
  module: ModuleNode,
};

const edgeOptions = {
  animated: true,
  style: {
    strokeWidth: 2,
    stroke: '#3b82f6',
  },
};

export function RoadmapFlow({ modules }: RoadmapFlowProps) {
  const createNodes = useCallback((): Node[] => {
    const nodes: Node[] = [];
    const startY = 50;
    const moduleSpacing = 180;
    const centerX = window.innerWidth / 3;
    
    modules.forEach((module, moduleIndex) => {
      const moduleY = startY + moduleIndex * moduleSpacing;
      const xOffset = moduleIndex % 2 === 0 ? -50 : 50; // Slight zigzag effect
      
      nodes.push({
        id: `module-${moduleIndex}`,
        type: 'module',
        position: { x: centerX + xOffset, y: moduleY },
        data: { 
          label: `Module ${moduleIndex + 1}: ${module.title}`,
          lessons: module.lessons
        },
      });
    });

    return nodes;
  }, [modules]);

  const createEdges = useCallback((): Edge[] => {
    const edges: Edge[] = [];

    modules.forEach((_, moduleIndex) => {
      if (moduleIndex < modules.length - 1) {
        edges.push({
          id: `edge-${moduleIndex}`,
          source: `module-${moduleIndex}`,
          target: `module-${moduleIndex + 1}`,
          type: 'smoothstep',
          animated: true,
          style: { 
            stroke: 'url(#gradient)',
            strokeWidth: 3,
          },
        });
      }
    });

    return edges;
  }, [modules]);

  return (
    <div className="h-[calc(100vh-2rem)] bg-gradient-to-br from-blue-50/50 via-white to-blue-50/50 rounded-xl overflow-hidden border border-blue-100/50 shadow-inner">
      <svg width="0" height="0">
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#1d4ed8" />
          </linearGradient>
        </defs>
      </svg>
      <ReactFlow
        nodes={createNodes()}
        edges={createEdges()}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={edgeOptions}
        connectionMode={ConnectionMode.Loose}
        fitView
        minZoom={0.5}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background 
          color="#93c5fd" 
          size={1.5}
          gap={16} 
          className="opacity-[0.015]" 
        />
        <Controls 
          className="!bg-white/80 !backdrop-blur-sm !shadow-lg !border !border-blue-100/50 !rounded-xl !p-2"
          showInteractive={false}
        />
      </ReactFlow>
    </div>
  );
}